import { ActionReference, DefaultGameState, GameObjectReference, GameState, Username } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { animations, pixelCornersCss } from "../helpers/cssImports";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "pixel";
        font-size: 40px;
        width: 80%;
        max-width: 1600px;
        height: 100vh;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto 40% 1fr auto;
        grid-column-gap: 0px;
        grid-row-gap: 10px;
        overflow: hidden;
    }

    ${animations}

    .cursor {
        animation: blink 500ms steps(1) infinite;
    }

    .title {
        font-family: "ka";
        text-align: center;
        margin-top: 10px;
        background-color: rgba(0, 0, 0, 0.8);
        padding: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        backdrop-filter: blur(10px);
    }

    .title p {
        padding: 0;
        margin: 0;
        font-size: 30px;
    }

    .header {
        height: 40vh;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        background-color: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(10px);
    }

    .header img {
        width: 100%;
        height: 100%;
        image-rendering: pixelated;
        object-fit: contain;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        display: flex;
        flex-direction: column;
        padding: 30px;
        height: 100%;
        overflow: auto;
        font-size: 30px;
        line-height: 0.7;
    }

    .container {
        display: flex;
        flex-direction: column;
        background-color: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(10px);
        overflow: auto;
    }

    .content p {
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        background-color: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(10px);
        display: flex;
        height: 15vh;
    }

    .buttons .game-objects-container {
        margin-bottom: 20px;
    }

    .footer .buttons {
        display: flex;
        height: 100%;
        width: 100%;
        flex-direction: column;
        overflow: auto;
        padding: 20px 10px 10px;
        text-shadow: 
        -2px -2px 0 black,  
        2px -2px 0 black,  
        -2px  2px 0 black,  
        2px  2px 0 black;
        font-size: 30px;
    }

    .footer .buttons::-webkit-scrollbar {
        width: 15px;
    }

    .content::-webkit-scrollbar {
        width: 21px;
    }

    .footer .buttons::-webkit-scrollbar-track {
        margin: 5px 0 25px 0;
    }

    .content::-webkit-scrollbar-track {
        margin: 15px;
    }

    .footer .buttons::-webkit-scrollbar-thumb, .content.content::-webkit-scrollbar-thumb {
        background: white;
        border: 4px solid black;
    }

    .content.content::-webkit-scrollbar-thumb {
        background: white;
        border-right: 10px solid black;
    }


    .footer .button {
        box-shadow: -4px 0 0 0 white,
            4px 0 0 0 white,
            0 -4px 0 0 white,
            0 4px 0 0 white;
        padding: 5px 10px;
        margin: 0 10px 20px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        transition: background-color 0.3s ease-in-out;
    }

    .game-object {
        background-color:rgb(111, 178, 255);
        animation: colorCycle2 15s linear infinite, hover2 0.5s infinite; 
        
    }

    .action {
        background-color:rgb(15, 122, 237);
        animation: colorCycle 15s linear infinite; 
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: black;
        animation: hover 0.5s ease-in-out 2 forwards, glow 0.5s infinite;
    }

    .combine {
        animation: greenGlow 0.5s infinite;
    }

    .username {
        font-family: "pixel";
        position: fixed;
        left: 10%;
        top: 25%;
    }

    note-book {
        margin-right: auto;
    }

${pixelCornersCss}
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    private _lastTypedText: string[] = [];

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        await this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private async updateGameState(state: GameState): Promise<void> {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        await this.render();
    }

    /**
     * Render the contents of this page
     */
    private async render(): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
            ${await this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        // Render content with type effecta
        this.renderTextContent();

        // Renders the background image
        this.renderBackground(this._currentGameState?.background);
    }

    /**
     * Renders the background image
     *
     * @param imageURL - The URL of the image
     */
    private renderBackground(imageURL: string | undefined): void {
        if (!imageURL) return;
        document.body.style.backgroundImage = `url("${imageURL}")`;
    }

    private renderTextContent(): void {
        const content: string[] = this._currentGameState?.text || [];
        if (content !== this._lastTypedText) {
            this._lastTypedText = content;
            void this.typeEffect("typing-text", content);
        }
        else {
            const mainElement: HTMLElement | null = this.shadowRoot?.getElementById("typing-text") as HTMLElement | null;
            if (!mainElement) return;
            mainElement.innerHTML = content.map(p => `<p>${p}</p>`).join("");
        }
    }

    /**
     * Creates a delay
     *
     * @returns - Promise that resolves in 10ms
     */
    private delay(): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, 10));
    }

    /**
     * Applies type effect to an element
     *
     * @param elementId - Id of an element to apply type effect on
     * @param content - Array of strings each representing a paragrph
     */
    private async typeEffect(elementId: string, content: string[]): Promise<void> {
        const mainElement: HTMLElement | null = this.shadowRoot?.getElementById(elementId) as HTMLElement | null;
        if (!mainElement) return;

        mainElement.innerHTML = "";
        const cursor: string = "<span class='cursor'>|</span>";

        // Loop through each paragrah in content array
        for (const paragraphText of content) {
            const pElement: HTMLElement = document.createElement("p");
            mainElement.appendChild(pElement);

            let placeholder: string = "";

            // Loop thourgh each characte in the paragraph text
            for (const char of paragraphText) {
                placeholder += char;
                pElement.innerHTML = placeholder + cursor;
                await this.delay(); // Delay before adding nect character
            }
            pElement.innerHTML = placeholder;
            await this.delay(); // Delay before adding nect paragraph
        }
        mainElement.innerHTML += cursor;
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private async renderTitle(): Promise<string> {
        const roomName: string | undefined = this._currentGameState?.roomName;
        const username: Username[] = await this._gameRouteService.getLoggedInUsername(
            { userId: (parseInt(localStorage.getItem("userId") ?? "")) });
        if (roomName) {
            return `
            <div class="title pixel-corners">
                <log-out></log-out>
                <span class="username">Welcome, ${username[0].username}!</span>
                <p>${roomName}</p>
                <try-again></try-again>
            </div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header pixel-corners--wrapper">
                <note-book></note-book>
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="pixel-corners--wrapper container">
                    <div class="content" id="typing-text">
                    <p></p>
                    </div>
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer pixel-corners">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div class="game-objects-container">
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button action ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button game-object ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            await this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        await this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        await this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        await this.updateGameState(state);
    }
}
